import firebase from "firebase/compat/app";
import "firebase/compat/auth";
import type { Store } from "redux";
import * as userActions from "./actions/user";
import { clearSessionAccessToken } from "./firebaseToken";

export const SERVICE_ACCESS_TOKEN_WAIT_MS = 8_000;
export const AUTH_READY_GRACE_MS = 5_000;
export const REHYDRATE_TIMEOUT_MS = 10_000;

type RecoveryStore = Pick<Store<unknown>, "getState" | "dispatch" | "subscribe">;

let recoveryStore: RecoveryStore | null = null;
let sessionRecoveryStarted = false;
let pageshowListenerInstalled = false;

function isClientBrowser(): boolean {
  return (
    typeof window !== "undefined" &&
    typeof window.addEventListener === "function" &&
    typeof window.setTimeout === "function" &&
    typeof window.clearTimeout === "function"
  );
}

export function installStaleAuthRecoveryPageshowReset(): void {
  if (pageshowListenerInstalled) {
    return;
  }
  if (!isClientBrowser()) {
    return;
  }
  pageshowListenerInstalled = true;
  window.addEventListener("pageshow", () => {
    sessionRecoveryStarted = false;
  });
}

export function registerStaleAuthRecoveryStore(store: RecoveryStore): void {
  recoveryStore = store;
}

function isSessionExpiredLoginUrl(): boolean {
  if (!isClientBrowser()) {
    return false;
  }
  try {
    return new URLSearchParams(window.location.search).get("session") === "expired";
  } catch {
    return false;
  }
}

function shouldWipeLocalStorageKey(key: string): boolean {
  const lower = key.toLowerCase();
  if (key === "persist:primary") {
    return true;
  }
  if (lower.startsWith("firebase:") || lower.startsWith("firebasehost:")) {
    return true;
  }
  return lower.indexOf("viewspark") !== -1;
}

/**
 * Remove ViewSpark persist, Firebase Auth, and table-pref keys from localStorage.
 */
export function wipeViewsparkLocalStorage(): void {
  if (typeof window === "undefined" || !window.localStorage) {
    return;
  }
  const keys: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key && shouldWipeLocalStorageKey(key)) {
      keys.push(key);
    }
  }
  keys.forEach(key => {
    window.localStorage.removeItem(key);
  });
}

function persistRehydrated(store: RecoveryStore): boolean {
  const persist = (store.getState() as { _persist?: { rehydrated?: boolean } })
    ._persist;
  return persist?.rehydrated === true;
}

/**
 * If redux-persist has not rehydrated after {@link REHYDRATE_TIMEOUT_MS}, wipe
 * ViewSpark localStorage and send the user to the session-expired login.
 */
export function watchPersistRehydrateTimeout(store: RecoveryStore): void {
  if (!isClientBrowser()) {
    return;
  }
  if (isSessionExpiredLoginUrl()) {
    return;
  }
  if (persistRehydrated(store)) {
    return;
  }
  let timeoutId: number | null = null;
  const unsubscribe = store.subscribe(() => {
    if (!persistRehydrated(store)) {
      return;
    }
    if (timeoutId != null) {
      window.clearTimeout(timeoutId);
      timeoutId = null;
    }
    unsubscribe();
  });
  timeoutId = window.setTimeout(() => {
    timeoutId = null;
    unsubscribe();
    wipeViewsparkLocalStorage();
    forceAdminSessionRecovery();
  }, REHYDRATE_TIMEOUT_MS);
}

export function forceAdminSessionRecovery(query = "session=expired"): void {
  if (typeof window === "undefined") {
    return;
  }
  if (sessionRecoveryStarted) {
    return;
  }
  sessionRecoveryStarted = true;
  clearSessionAccessToken();
  const remember = (recoveryStore?.getState() as { user?: { remember?: boolean } })
    ?.user?.remember;
  recoveryStore?.dispatch(userActions.logoutCurrentUser(remember === true));
  void firebase.auth().signOut().catch(() => {});
  const firebaseApi = firebase as unknown as { logout?: () => Promise<void> };
  if (typeof firebaseApi.logout === "function") {
    void firebaseApi.logout().catch(() => {});
  }
  window.location.replace(`/login?${query}`);
}
